import React from 'react';
import { Button } from '@mui/material';
import { Print } from '@mui/icons-material';
import { formatNumberWithComma } from '../../../utils/helpers';
import { companyInfo } from '../../../utils/dataInfo';

const PrintReceipt = ({ orderData, disabled = false }) => {
const handlePrint = () => {
    // POS paper dimensions: 80mm width (approx 300px)
    // Adjustable variables
    const PAPER_WIDTH = '80mm';
    const FONT_SIZE_SMALL = '10px';
    const FONT_SIZE_MEDIUM = '12px';
    const FONT_SIZE_LARGE = '14px';
    const LINE_HEIGHT = '1.2';
    const MARGIN = '3mm';
    const BORDER_STYLE = 'dashed';
    
    // Calculate subtotal
    const subtotal = orderData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Create printable HTML content optimized for POS paper
    const printContent = `
    <!DOCTYPE html>
    <html>
        <head>
        <title>Receipt #${orderData.id || 'ONSITE'}</title>
        <style>
            @page {
            size: ${PAPER_WIDTH} auto;
            margin: ${MARGIN};
            }
            
            * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            }
            
            body {
            font-family: 'Courier New', monospace;
            width: ${PAPER_WIDTH};
            font-size: ${FONT_SIZE_SMALL};
            line-height: ${LINE_HEIGHT};
            }
            
            .receipt {
            width: 100%;
            overflow: hidden;
            }
            
            .header {
            text-align: center;
            margin-bottom: 5px;
            padding-bottom: 5px;
            border-bottom: 1px ${BORDER_STYLE} #000;
            }
            
            .company-name {
            font-size: ${FONT_SIZE_LARGE};
            font-weight: bold;
            margin-bottom: 2px;
            text-transform: uppercase;
            }
            
            .company-info {
            font-size: 9px;
            margin-bottom: 2px;
            }
            
            .title {
            text-align: center;
            font-size: ${FONT_SIZE_MEDIUM};
            font-weight: bold;
            margin: 5px 0;
            text-transform: uppercase;
            }
            
            .order-info {
            display: flex;
            justify-content: space-between;
            margin: 4px 0;
            font-size: ${FONT_SIZE_SMALL};
            }
            
            .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 5px 0;
            font-size: ${FONT_SIZE_SMALL};
            }
            
            .items-table th {
            text-align: left;
            border-bottom: 1px solid #000;
            padding: 2px 0;
            font-weight: bold;
            }
            
            .items-table td {
            padding: 2px 0;
            border-bottom: 1px dotted #ccc;
            }
            
            .item-qty {
            text-align: center;
            width: 15%;
            }
            
            .item-price {
            text-align: right;
            width: 25%;
            }
            
            .item-total {
            text-align: right;
            width: 30%;
            }
            
            .totals {
            border-top: 1px ${BORDER_STYLE} #000;
            margin-top: 5px;
            padding-top: 5px;
            }
            
            .total-row {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
            }
            
            .total-row.total {
            font-weight: bold;
            font-size: ${FONT_SIZE_MEDIUM};
            margin-top: 3px;
            }
            
            .payment-info {
            margin: 5px 0;
            padding: 3px 0;
            border-top: 1px ${BORDER_STYLE} #000;
            border-bottom: 1px ${BORDER_STYLE} #000;
            }
            
            .footer {
            text-align: center;
            margin-top: 8px;
            font-size: 9px;
            line-height: 1.1;
            }
            
            .separator {
            text-align: center;
            margin: 3px 0;
            font-weight: bold;
            }
            
            .thank-you {
            text-align: center;
            margin: 5px 0;
            font-weight: bold;
            font-style: italic;
            }
            
            .cut-line {
            border-top: 1px dashed #000;
            margin: 10px 0 5px 0;
            text-align: center;
            }
            
            .cut-line-text {
            position: relative;
            top: -8px;
            background: white;
            padding: 0 5px;
            font-size: 8px;
            }
            
            /* Force line breaks */
            .word-wrap {
            word-wrap: break-word;
            overflow-wrap: break-word;
            }
            
            /* Compact layout */
            .compact {
            margin: 1px 0;
            }
        </style>
        </head>
        <body>
        <div class="receipt">
            <!-- Header with Company Info -->
            <div class="header">
            <div class="company-name">${companyInfo.name}</div>
            <div class="company-info">${companyInfo.address}</div>
            <div class="company-info">Tel: ${companyInfo.phone}</div>
            ${companyInfo.taxId ? `<div class="company-info">${companyInfo.taxId}</div>` : ''}
            </div>
            
            <!-- Receipt Title -->
            <div class="title">SALES RECEIPT</div>
            
            <!-- Order Information -->
            <div class="order-info">
            <div>
                <div class="compact">Date: ${new Date(orderData.date || Date.now()).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
                })}</div>
                <div class="compact">Receipt: ${orderData.id || 'ONSITE'}</div>
            </div>
            <div style="text-align: right;">
                ${orderData.orderType ? `<div class="compact">Type: ${orderData.orderType}</div>` : ''}
                ${orderData.servant ? `<div class="compact">Servant: ${orderData.servant}</div>` : ''}
            </div>
            </div>
            
            <!-- Customer Information -->
            ${orderData.customerName ? `
            <div class="order-info">
                <div class="word-wrap">Customer: ${orderData.customerName}</div>
            </div>
            ` : ''}
            <div class="separator">---------------------------</div>
            
            <!-- Items Table -->
            <table class="items-table">
            <thead>
                <tr>
                <th>Item</th>
                <th class="item-qty">Qty</th>
                <th class="item-price">Price</th>
                <th class="item-total">Total</th>
                </tr>
            </thead>
            <tbody>
                ${orderData.items.map(item => `
                <tr>
                    <td class="word-wrap">${item.name}</td>
                    <td class="item-qty">${formatNumberWithComma(item.quantity)}</td>
                    <td class="item-price">${formatNumberWithComma(item.price)}</td>
                    <td class="item-total">${formatNumberWithComma(item.price * item.quantity)}</td>
                </tr>
                `).join('')}
            </tbody>
            </table>
            
            <div class="separator">---------------------------</div>
            
            <!-- Totals Section -->
            <div class="totals">
            <div class="total-row">
                <span>Subtotal:</span>
                <span>${formatNumberWithComma(subtotal)}</span>
            </div>
            <div class="total-row">
                <span>Tax:</span>
                <span>0.00</span>
            </div>
            <div class="total-row total">
                <span>TOTAL:</span>
                <span>${formatNumberWithComma(orderData.total)}</span>
            </div>
            </div>
            
            <!-- Payment Information -->
            <div class="payment-info">
            <div class="order-info">
                <div>Payment: ${orderData.paymentMethod?.toUpperCase() || 'CASH'}</div>
            </div>
            ${orderData.paymentMethod === 'cash' ? `
                <div class="order-info">
                <div>Amount Received:</div>
                <div>${formatNumberWithComma(orderData.total)}</div>
                </div>
                <div class="order-info">
                <div>Change:</div>
                <div>0.00</div>
                </div>
            ` : ''}
            </div>
            
            <!-- Footer -->
            <div class="footer">
            <div>${companyInfo.receiptFooter || 'Thank you for your business!'}</div>
            <div>Items can be exchanged within 7 days with receipt</div>
            <div>Keep your receipt for warranty purposes</div>
            </div>
            
            <div class="thank-you">THANK YOU!</div>
            
            <!-- Cut line for POS paper -->
            <div class="cut-line">
            <div class="cut-line-text">CUT HERE</div>
            </div>
            
            <!-- Duplicate for customer copy -->
            <div style="margin-top: 20px;">
            <div class="title">CUSTOMER COPY</div>
            <div class="order-info">
                <div>
                <div class="compact">Date: ${new Date(orderData.date || Date.now()).toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })}</div>
                <div class="compact">Receipt: ${orderData.id || 'ONSITE'}</div>
                </div>
            </div>
            <div class="separator">---------------------------</div>
            <div class="total-row total">
                <span>TOTAL:</span>
                <span>${formatNumberWithComma(orderData.total)}</span>
            </div>
            <div class="thank-you">THANK YOU!</div>
            </div>
        </div>
        </body>
    </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank', 'width=350,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
    }, 250);
};

return (
    <Button
    variant="contained"
    color="secondary"
    startIcon={<Print />}
    onClick={handlePrint}
    disabled={disabled}
    sx={{ 
        mt: 1,
        backgroundColor: '#4CAF50',
        '&:hover': {
        backgroundColor: '#388E3C'
        }
    }}
    fullWidth
    >
    Print Receipt
    </Button>
);
};

export default PrintReceipt;